import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { X, Download, Save, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuizReportModal = ({ 
  quizData,
  quizAnswers, 
  currentQuiz,
  subject,
  difficulty,
  gradeLevel,
  timeTaken,
  onClose,
  onSaveComplete
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quizId, setQuizId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [institutionName, setInstitutionName] = useState(null);

  const correctAnswers = currentQuiz.filter((q, idx) => quizAnswers[idx] === q.correct_answer).length;
  const wrongAnswers = currentQuiz.length - correctAnswers;
  const scorePercentage = (correctAnswers / currentQuiz.length) * 100;

  const minutes = Math.floor(timeTaken / 60);
  const seconds = timeTaken % 60;

  // Extract grade from curriculum if available, otherwise use passed gradeLevel
  const extractGradeFromCurriculum = () => {
    if (quizData?.curriculum) {
      const match = quizData.curriculum.match(/Grade\s+(\d+(?:st|nd|rd|th)?)/i);
      if (match) {
        return match[1];
      }
    }
    return gradeLevel || '10th';
  };

  const actualGradeLevel = extractGradeFromCurriculum();

  const questionsWithAnswers = currentQuiz.map((q, idx) => ({
    question: q.question,
    options: q.options,
    selected_answer: quizAnswers[idx] || 'Not answered',
    correct_answer: q.correct_answer,
    is_correct: quizAnswers[idx] === q.correct_answer,
    explanation: q.explanation,
    topic: q.topic || 'General'
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/quiz/submit-detailed`, {
        subject: subject,
        difficulty: difficulty,
        grade_level: actualGradeLevel,
        total_questions: currentQuiz.length,
        correct_answers: correctAnswers,
        wrong_answers: wrongAnswers,
        score_percentage: scorePercentage,
        time_taken_seconds: timeTaken,
        questions_with_answers: questionsWithAnswers,
        curriculum: quizData?.curriculum || ''
      });

      const savedQuizId = response.data.quiz_id;
      setQuizId(savedQuizId);
      
      // Set institution name if returned
      if (response.data.institution_name) {
        setInstitutionName(response.data.institution_name);
      }

      // Get AI analysis
      const analysisResponse = await axios.post(`${API}/quiz/analyze`, {
        quiz_id: savedQuizId
      });
      
      setAnalysis(analysisResponse.data.analysis);
      toast.success('Quiz saved and analyzed successfully!');
      
      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error) {
      toast.error('Failed to save quiz');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndDownload = async () => {
    setSaving(true);
    try {
      // First save if not already saved
      let downloadQuizId = quizId;
      
      if (!quizId) {
        const response = await axios.post(`${API}/quiz/submit-detailed`, {
          subject: subject,
          difficulty: difficulty,
          grade_level: actualGradeLevel,
          total_questions: currentQuiz.length,
          correct_answers: correctAnswers,
          wrong_answers: wrongAnswers,
          score_percentage: scorePercentage,
          time_taken_seconds: timeTaken,
          questions_with_answers: questionsWithAnswers,
          curriculum: quizData?.curriculum || ''
        });
        
        downloadQuizId = response.data.quiz_id;
        setQuizId(downloadQuizId);
        
        // Set institution name if returned
        if (response.data.institution_name) {
          setInstitutionName(response.data.institution_name);
        }

        // Get AI analysis
        const analysisResponse = await axios.post(`${API}/quiz/analyze`, {
          quiz_id: downloadQuizId
        });
        
        setAnalysis(analysisResponse.data.analysis);
      }

      // Download PDF
      const pdfResponse = await axios.get(`${API}/quiz/report/${downloadQuizId}/pdf`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quiz_report_${downloadQuizId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Quiz saved and PDF downloaded!');
      
      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error) {
      toast.error('Failed to save and download quiz');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl bg-white p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Quiz Report</h2>
            <p className="text-sm text-gray-500">
              {subject.charAt(0).toUpperCase() + subject.slice(1)} - {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
              {actualGradeLevel && <span className="ml-2 text-teal-600 font-medium">Grade {actualGradeLevel}</span>}
            </p>
            {institutionName && (
              <p className="text-xs text-gray-400 mt-1">
                📚 {institutionName}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {/* Summary Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Score</p>
              <p className="text-3xl font-bold text-green-600">{scorePercentage.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">{correctAnswers}/{currentQuiz.length} correct</p>
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Time Taken</p>
              <p className="text-3xl font-bold text-blue-600">{minutes}m {seconds}s</p>
              <p className="text-xs text-gray-500 mt-1">{(timeTaken / currentQuiz.length).toFixed(1)}s per question</p>
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Results</p>
              <div className="flex justify-center gap-2 mt-2">
                <Badge className="bg-green-500">{correctAnswers} Correct</Badge>
                <Badge className="bg-red-500">{wrongAnswers} Wrong</Badge>
              </div>
            </div>
          </Card>
        </div>

        {/* AI Analysis Section */}
        {analysis && (
          <Card className="p-4 mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <h3 className="text-lg font-semibold text-indigo-900 mb-3">AI Performance Analysis</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Overall Assessment</p>
                <p className="text-sm text-gray-600">{analysis.overall_assessment}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Proficiency Level</p>
                <Badge className="bg-indigo-500">{analysis.proficiency_level}</Badge>
              </div>
              
              {analysis.strengths && analysis.strengths.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-700">Strengths</p>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {analysis.strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              
              {analysis.weaknesses && analysis.weaknesses.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700">Areas for Improvement</p>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {analysis.weaknesses.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              
              {analysis.next_steps && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Next Steps</p>
                  <p className="text-sm text-gray-600">{analysis.next_steps}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <Button 
            onClick={handleSave}
            disabled={saving || quizId !== null}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Save className="mr-2" size={18} />
            {quizId ? 'Saved' : saving ? 'Saving...' : 'Save Report'}
          </Button>
          
          <Button 
            onClick={handleSaveAndDownload}
            disabled={saving}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
          >
            <Download className="mr-2" size={18} />
            {saving ? 'Processing...' : 'Save & Download PDF'}
          </Button>
        </div>

        {/* Details Toggle */}
        <Button
          onClick={() => setShowDetails(!showDetails)}
          variant="outline"
          className="w-full mb-4"
        >
          {showDetails ? <ChevronUp className="mr-2" size={18} /> : <ChevronDown className="mr-2" size={18} />}
          {showDetails ? 'Hide' : 'Show'} Detailed Answers
        </Button>

        {/* Detailed Q&A Section */}
        {showDetails && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Question-by-Question Review</h3>
            {questionsWithAnswers.map((qa, idx) => (
              <Card 
                key={idx} 
                className={`p-4 ${qa.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="font-semibold text-gray-900">Question {idx + 1}</p>
                  <Badge className={qa.is_correct ? 'bg-green-500' : 'bg-red-500'}>
                    {qa.is_correct ? '✓ Correct' : '✗ Wrong'}
                  </Badge>
                </div>
                
                <p className="text-gray-800 mb-3">{qa.question}</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 min-w-[120px]">Your Answer:</span>
                    <span className={qa.is_correct ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                      {qa.selected_answer}
                    </span>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 min-w-[120px]">Correct Answer:</span>
                    <span className="text-green-700 font-medium">{qa.correct_answer}</span>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 min-w-[120px]">Topic:</span>
                    <span className="text-gray-600">{qa.topic}</span>
                  </div>
                  
                  {qa.explanation && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <p className="font-medium text-gray-700">Explanation:</p>
                      <p className="text-gray-600 italic">{qa.explanation}</p>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default QuizReportModal;